import { SSEError, subscribeToSSE } from "@albertine/api-client";
import { type LiveUpdateTopic, LiveUpdateTopicSchema } from "./topics";

type SubscribeToLiveUpdatesOptions = {
    url: string;
    abortSignal: AbortSignal;
    getAccessToken: (options?: { forceRefresh?: boolean }) => Promise<string | null>;
    onUpdate: (topic: LiveUpdateTopic) => void;
    onResync: () => void;
    /** The gap after a failure can be long (network drop, sleep), so whatever is shown may be stale. */
    onReconnectAfterError: () => void;
};

export async function subscribeToLiveUpdates(
    options: SubscribeToLiveUpdatesOptions,
): Promise<void> {
    const { url, abortSignal, getAccessToken } = options;
    let failed = false;

    try {
        const stream = subscribeToSSE({
            url,
            abortSignal,
            getAccessToken,
            onError: () => {
                failed = true;
            },
            shouldRetry: (error) => !isClientError(error),
        });
        for await (const event of stream) {
            switch (event.type) {
                case "ready":
                    if (failed) {
                        failed = false;
                        options.onReconnectAfterError();
                    }
                    break;
                case "update": {
                    const topic = LiveUpdateTopicSchema.safeParse(
                        tryParseJson(event.data),
                    );
                    if (topic.success) {
                        options.onUpdate(topic.data);
                    }
                    break;
                }
                case "resync":
                    options.onResync();
                    break;
                default:
                    break;
            }
        }
    } catch (error) {
        console.warn("[live-updates] Giving up:", error);
    }
}

function isClientError(error: unknown): boolean {
    return error instanceof SSEError && error.status >= 400 && error.status < 500;
}

/** A throw inside the `for await` would end the stream instead of reconnecting it. */
function tryParseJson(payload: string): unknown {
    try {
        return JSON.parse(payload);
    } catch {
        return undefined;
    }
}
